import { Dispatch, RefObject, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import {
  createFutureContract,
  CreateFutureContractProps,
  getFutureContract,
  getFutureContracts,
  GetFutureContractsProps,
  updateFutureContract,
  UpdateFutureContractProps,
} from "@/services/future-contract.service";
import {
  FutureContractDetail,
  FutureContractListResponse,
} from "./future-contract.schema";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { FCFRProps, getFutureContractsFilter } from "./filter-obj";
import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { AppRoutes } from "@/constants/routes";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { EstadosContratoFuturo } from "@/constants/enums/status/entity-status-ids";

export interface FetchFutureContractsHandlerProps {
  setLoadingFutureContracts: Dispatch<SetStateAction<boolean>>;
  filters?: GetFutureContractsProps;
  showLoad?: boolean;
  setFutureContracts: Dispatch<
    SetStateAction<FutureContractListResponse | undefined>
  >;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFutureContractsHandler = async ({
  setLoadingFutureContracts,
  filters,
  showLoad,
  setFutureContracts,
  navigate,
  fetchPermissions,
}: FetchFutureContractsHandlerProps) => {
  try {
    if (showLoad) setLoadingFutureContracts(true);

    const response = await getFutureContracts({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setFutureContracts(response);

    setLoadingFutureContracts(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo obtener las solicitudes de granos a entregar.",
      });
    }
    throw error;
  } finally {
    setLoadingFutureContracts(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("future_contract_date", undefined);
  setValue("account_number", undefined);
  setValue("future_contract_amount", "");
  setValue("estados", []);
  setValue("ton", "");
  setValue("grain", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetFutureContractsProps> | undefined>
  >;
  fetchFutureContracts: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchFutureContracts,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetFutureContractsProps> = getFutureContractsFilter(
    event as FCFRProps,
  );

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchFutureContracts(filterObj);

  setOpenFilters(false);
};

export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

export const getOptionsHandler = async ({
  inputValue,
}: GetAccountOptionsHandlerProps) => {
  const filtered = await getCuentasByBusqueda({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
      value: p.nroCuenta,
      tipoCuenta: p.tipoCuenta,
      idTipoCuenta: p.idTipoCuenta,
    })) ?? []
  );
};

export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoadingPersons,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue.trim().length < 3) {
        resolve([]);
        return;
      }

      setLoadingPersons(true);
      const options = getOptions(inputValue);

      setLoadingPersons(false);
      resolve(options);
    }, 1000);
  });
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  >;
}

export interface CreateFutureContractHandlerProps {
  navigate: NavigateFunction;
  setLoadingCreateFutureContract: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  fetchPermissions: () => Promise<void>;
}

export const createFutureContractHandler = async ({
  navigate,
  setLoadingCreateFutureContract,
  fetchPermissions,
  watchedValues,
}: CreateFutureContractHandlerProps) => {
  setLoadingCreateFutureContract(true);

  try {
    const parsedTon =
      typeof watchedValues?.ton === "string"
        ? Number.parseFloat(watchedValues.ton.replace(",", "."))
        : watchedValues?.ton;

    const objToSend: CreateFutureContractProps = {
      nroCuentaOrigen: watchedValues?.account?.value,
      tipoFijacion: watchedValues?.fixation_type?.[0],
      grano: watchedValues?.grain,
      toneladas: parsedTon,
      cosecha: watchedValues?.harvest?.[0],
      monto: formatCurrencyInputToNumber(watchedValues?.price ?? 0),
      esCanje: watchedValues?.isExchange,
      mensaje: watchedValues?.order_detail,
      entrega: watchedValues?.delivery_month,
      tipoMoneda: watchedValues?.currency_type,
    };

    await createFutureContract(objToSend);

    toaster.create({
      title: "Venta de granos a futuro creada con éxito",
      type: "success",
    });
    setLoadingCreateFutureContract(false);
    navigate(AppRoutes.GRAIN_FUTURE_CONTRACTS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.GRAIN_FUTURE_CONTRACTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se ha podido crear la venta de granos a futuro.",
        ),
        duration: 6000,
      });
    }
    throw error;
  } finally {
    setLoadingCreateFutureContract(false);
  }
};

export interface HandleSendNegotiationHandlerProps {
  setLoadingSendNegotiation: Dispatch<SetStateAction<boolean>>;
  futureContract?: FutureContractDetail | null;
  fetchFutureContract: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const sendFutureContractNegotiationHandler = async ({
  setLoadingSendNegotiation,
  futureContract,
  fetchFutureContract,
  navigate,
  fetchPermissions,
}: HandleSendNegotiationHandlerProps) => {
  setLoadingSendNegotiation(true);
  try {
    const objToSend: UpdateFutureContractProps = {
      idSolicitud: futureContract?.id ?? 0,
      estado: EstadosContratoFuturo.Negociacion,
    };

    await updateFutureContract(objToSend);

    toaster.create({
      type: "success",
      title: "Solicitud enviada a negociación.",
    });

    await fetchFutureContract(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo enviar la solicitud a negociación.",
      });
      throw error;
    }
  } finally {
    setLoadingSendNegotiation(false);
  }
};

export interface HandleSendCommunicationHandlerProps {
  setLoadingSendCommunication: Dispatch<SetStateAction<boolean>>;
  event?: FieldValues;
  futureContract?: FutureContractDetail | null;
  fetchFutureContract: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setOpenCommunicationModal: Dispatch<SetStateAction<boolean>>;
}

export const sendFutureContractCommunicationHandler = async ({
  setLoadingSendCommunication,
  event,
  futureContract,
  fetchFutureContract,
  navigate,
  fetchPermissions,
  setOpenCommunicationModal,
}: HandleSendCommunicationHandlerProps) => {
  setLoadingSendCommunication(true);
  try {
    const objToSend: UpdateFutureContractProps = {
      idSolicitud: futureContract?.id ?? 0,
      estado: EstadosContratoFuturo.EnComunicacion,
      mensaje: event?.request_detail,
    };

    await updateFutureContract(objToSend);

    toaster.create({
      type: "success",
      title: "Comunicado realizado exitosamente",
    });

    await fetchFutureContract(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "Hubo un error al enviar el comunicado.",
      });
      throw error;
    }
  } finally {
    setLoadingSendCommunication(false);
  }

  setOpenCommunicationModal(false);
};

export interface HandleRejectFutureContractHandlerProps {
  event?: FieldValues;
  setLoadingRejectFutureContract: Dispatch<SetStateAction<boolean>>;
  futureContract?: FutureContractDetail | null;
  navigate: NavigateFunction;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  fetchPermissions: () => Promise<void>;
}
export const handleRejectFutureContractHandler = async ({
  setLoadingRejectFutureContract,
  event,
  futureContract,
  navigate,
  setOpenRejectModal,
  fetchPermissions,
}: HandleRejectFutureContractHandlerProps) => {
  setLoadingRejectFutureContract(true);
  try {
    const objToSend: UpdateFutureContractProps = {
      idSolicitud: Number(futureContract?.id),
      mensaje: event?.request_detail,
      estado: EstadosContratoFuturo.Rechazada,
    };

    await updateFutureContract(objToSend);

    toaster.create({
      type: "success",
      title: `Venta de granos a entregar rechazada exitosamente.`,
    });

    setLoadingRejectFutureContract(false);

    navigate(AppRoutes.GRAIN_FUTURE_CONTRACTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se pudo rechazar la venta de granos a futuro.",
      });
      throw error;
    }
  } finally {
    setLoadingRejectFutureContract(false);
  }

  setOpenRejectModal(false);
};

export interface HandleExecuteFutureContractProps {
  setLoadingExecuteFutureContract: Dispatch<SetStateAction<boolean>>;
  futureContract?: FutureContractDetail | null;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  watchedValues?: FieldValues
}

export const handleExecuteFutureContractHandler = async ({
  setLoadingExecuteFutureContract,
  navigate,
  futureContract,
  watchedValues,
  fetchPermissions,
}: HandleExecuteFutureContractProps) => {
  setLoadingExecuteFutureContract(true);
  try {
    const objToSend: UpdateFutureContractProps = {
      idSolicitud: futureContract?.id || 0,
      estado: EstadosContratoFuturo.Confirmada,
      precioFinal: formatCurrencyInputToNumber(watchedValues?.final_price)
    };

    await updateFutureContract(objToSend);

    toaster.create({
      type: "success",
      title: `Venta de granos a futuro confirmada exitosamente.`,
    });

    setLoadingExecuteFutureContract(false);

    navigate(AppRoutes.GRAIN_FUTURE_CONTRACTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj,
      });
    }
    throw error;
  } finally {
    setLoadingExecuteFutureContract(false);
  }
};

export interface UpdateFutureContractValuesHandlerProps {
  errors: any;
  setLoadingEdit: Dispatch<SetStateAction<boolean>>;
  futureContract: any;
  fetchFutureContract: (showLoad: boolean) => Promise<void>;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  field: "amount" | "ton" | "delivery";
}

export const updateFutureContractValuesHandler = async ({
  errors,
  setLoadingEdit,
  futureContract,
  fetchFutureContract,
  setIsEditing,
  navigate,
  fetchPermissions,
  watchedValues,
  field,
}: UpdateFutureContractValuesHandlerProps) => {
  if (field === "amount" && errors?.future_contract_amount) return;
  if (field === "ton" && errors?.future_contract_ton) return;
  if (field === "delivery" && errors?.future_contract_delivery_month) return;

  let payload: Partial<UpdateFutureContractProps> = {};
  let errorMessage = "No se pudo actualizar la solicitud.";

  if (field === "amount") {
    payload = {
      monto: formatCurrencyInputToNumber(watchedValues?.future_contract_amount),
    };
    errorMessage = "No se pudo editar el monto solicitado.";
  }

  if (field === "ton") {
    const toneladasNumber = Number(
      String(watchedValues?.future_contract_ton).replace(",", "."),
    );

    payload = {
      toneladas: toneladasNumber,
    };
    errorMessage = "No se pudieron editar las toneladas.";
  }

  if (field === "delivery") {
    payload = {
      entrega: watchedValues?.future_contract_delivery_month,
    };
    errorMessage = "No se pudo editar la entrega.";
  }
  try {
    setLoadingEdit(true);

    await updateFutureContract({
      idSolicitud: futureContract?.id ?? 0,
      ...payload,
    });

    await fetchFutureContract(false);

    setLoadingEdit(false);
    setIsEditing((prev) => !prev);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (handled) setIsEditing(false);
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }

    throw error;
  } finally {
    setLoadingEdit(false);
  }
};

export interface FetchFutureContractDetailHandlerProps {
  id?: string;
  showLoad: boolean;
  setLoadingFutureContract: Dispatch<SetStateAction<boolean>>;
  setFutureContract: Dispatch<
    SetStateAction<FutureContractDetail | null | undefined>
  >;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFutureContractDetailHandler = async ({
  fetchPermissions,
  navigate,
  setFutureContract,
  setLoadingFutureContract,
  showLoad,
  id,
}: FetchFutureContractDetailHandlerProps) => {
  try {
    if (showLoad) setLoadingFutureContract(true);
    const response = await getFutureContract({
      id: Number(id),
    });

    setFutureContract(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo obtener la solicitud.",
      });
    }

    throw error;
  } finally {
    setLoadingFutureContract(false);
  }
};
